// eslint-disable-next-line no-use-before-define
import React from 'react'
import { withTheme } from 'styled-components'
import { InfoIcon } from '@sainsburys-tech/bolt-icons'
import { getWrapperProps } from '../../../helpers/ComponentHelper'
import { createGolNonContentDataAttributes } from '../../../helpers/tracking'
import { colorToLunaToken } from '../../../helpers/theming/utilities'
import { C003Props } from './types'
import {
  StyledCTAContainer,
  StyledCTA,
  StyledButton,
  StyledHeadingWrapper,
  StyledHeadings,
  StyledWrapperAnchor,
  StyledWrapperDiv,
  StyledIconWrapper
} from './C003.styles'

/**
 * C003 is a skyline banner
 */
const C003 = ({
  heading,
  subheading,
  links = [],
  ctaStyle = 'link',
  onLinkClick,
  backgroundColor,
  textColor,
  buttonColor,
  buttonTextColor,
  theme,
  icon
}: C003Props): React.ReactElement => {
  const hasOneCTA = !!(links.length === 1 && links[0]?.copy)

  const [firstLink] = links
  const hasLink = !!firstLink?.url && links.length === 1
  const hyperLinkTitle = firstLink?.title
  const hyperLinkUrl = firstLink?.url

  const Wrapper = hasLink ? StyledWrapperAnchor : StyledWrapperDiv
  const CTA = firstLink && ctaStyle !== 'link' ? StyledButton : StyledCTA

  const addGolTracking = (evt, ctaCopy) => {
    const dataAttributes = createGolNonContentDataAttributes(
      'link',
      'click',
      'cta button',
      `C003-${ctaCopy.toLowerCase()}`,
      'interaction'
    )
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, url, ctaCopy) => {
    if (onLinkClick) {
      onLinkClick(event, url)
    }
    addGolTracking(event, ctaCopy)
  }

  return (
    <Wrapper
      data-test='c003-wrapper'
      as={hasLink ? 'a' : undefined}
      target={firstLink?.target}
      {...(hasLink && {
        ...getWrapperProps(hyperLinkTitle, hyperLinkUrl, (event) => ctaClick(event, hyperLinkUrl, firstLink.copy))
      })}
      backgroundColour={backgroundColor}
    >
      <StyledHeadings data-test='c003-text-container' hasIcon={!!icon} textColor={textColor} hasOneCTA={hasOneCTA}>
        <StyledHeadingWrapper>
          {icon === 'info' && (
            <StyledIconWrapper>
              <InfoIcon
                channel={theme.channel}
                data-test='c003-info-icon'
                height='1.5rem'
                width='1.5rem'
                fill={colorToLunaToken(textColor, theme) || theme.color.monochrome.darker}
              />
            </StyledIconWrapper>
          )}
          <h4 data-test='c003-heading'>{heading}</h4>
        </StyledHeadingWrapper>
        {subheading && <p data-test='c003-subeading'>{subheading}</p>}
      </StyledHeadings>
      {!!firstLink?.copy && (
        <StyledCTAContainer
          data-test='c003-cta-container'
          textColor={textColor}
          hasIcon={!!icon}
          hasOneCTA={hasOneCTA}
          kind={ctaStyle}
        >
          {links.map((linkElement, index) => (
            <CTA
              data-test={`c003-cta-${index}`}
              target={linkElement.target}
              as={!hasOneCTA && ctaStyle === 'link' && 'a'}
              {...getWrapperProps(linkElement.title, linkElement.url)}
              kind={ctaStyle}
              buttonColor={buttonColor}
              buttonTextColor={buttonTextColor}
              full={false}
              style={{
                marginTop: !hasOneCTA ? '16px' : undefined
              }}
              onClick={(evt) => !!linkElement?.url && ctaClick(evt, linkElement.url, linkElement.copy)}
            >
              {linkElement.copy}
            </CTA>
          ))}
        </StyledCTAContainer>
      )}
    </Wrapper>
  )
}

export default withTheme(C003)
